import * as React from "react";
import { cn } from "@finster/ui";
import { Flag } from "lucide-react";

interface ExcelHeaderProps {
  /**
   * Display name of the currently-focused workbook (filename only —
   * the caller is expected to strip any directory prefix). Empty
   * string or `undefined` suppresses the slot, e.g. before the
   * workbook-key probe resolves.
   */
  workbookName?: string;
  onOpenSupport: () => void;
  /** Currently selected header view when the chat/citations toggle is visible. */
  view?: "chat" | "citations";
  /** Called when the user switches between chat and citations. */
  onViewChange?: (view: "chat" | "citations") => void;
  /** Whether to render the compact chat/citations toggle beside the workbook name. */
  showViewToggle?: boolean;
}

export const ExcelHeader: React.FC<ExcelHeaderProps> = ({
  workbookName,
  onOpenSupport,
  view = "chat",
  onViewChange,
  showViewToggle = false,
}) => {
  const showWorkbook = Boolean(workbookName && workbookName.length > 0);
  // `sticky top-0` keeps the header glued to the top of the taskpane
  // while chat history scrolls underneath, robust if the outer layout
  // grows past `100vh`. `bg-background` prevents chat bubbles
  // scrolling under the header from visually bleeding through.
  return (
    <div
      className={`sticky top-0 z-20 flex-none bg-background px-4 pt-4 pb-3 flex items-center ${
        showWorkbook ? "justify-between" : "justify-end"
      }`}
    >
      {showWorkbook && (
        <div className="flex min-w-0 items-center gap-2">
          <span
            className="truncate text-[15px] font-medium text-foreground"
            title={workbookName}
          >
            {workbookName}
          </span>
          {showViewToggle && onViewChange && (
            <div
              role="tablist"
              aria-label="Chat or citations"
              className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-accent p-0.5"
            >
              {(["chat", "citations"] as const).map((nextView) => (
                <button
                  key={nextView}
                  type="button"
                  role="tab"
                  aria-selected={view === nextView}
                  onClick={() => onViewChange(nextView)}
                  className={cn(
                    "inline-flex h-5 min-w-[28px] cursor-pointer items-center justify-center rounded-md border-0 px-1.5 text-[10px] font-medium leading-[12px] transition-colors",
                    view === nextView
                      ? "bg-background text-foreground shadow-sm"
                      : "bg-transparent text-foreground/80 hover:text-foreground",
                  )}
                >
                  {nextView === "chat" ? "Chat" : "Citations"}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={onOpenSupport}
          title="Support"
          aria-label="Open support"
          className="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border-0 bg-transparent text-muted-foreground hover:bg-accent hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring-focus focus-visible:ring-offset-2"
        >
          <Flag className="pointer-events-none h-4 w-4" aria-hidden />
        </button>
      </div>
    </div>
  );
};
